import React, { useState, useEffect, useMemo, useCallback } from "react";
import { connect, useDispatch } from "react-redux";
import { OktaAuth } from "@okta/okta-auth-js";
import { withOktaAuth } from "@okta/okta-react";
import { ThemeProvider } from "styled-components";
import { useIdleTimer } from "react-idle-timer";
import IdleModal from "@/components/idle-modal";
import useLogoutTimers from "@/util/idleTimerUtils";
import { userIsLoggedIn } from "@/components/loginPage/LoginPage.selectors";
import { logoutFromInactivity } from "@/components/loginPage/LoginPage.state";
import Routes from "@/Routes";
import Overlay from "@/components/overlay";
import LoadingOverlay from "@/components/loadingOverlay";
import { getLoadingOverlayIsActive } from "./state/shared.state";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
  getUserEmail,
  getUserRole,
  getUserAgencies,
  getClientSlug,
  getClientBrand,
  getPOSEnabled,
  getPaymentPortalURL,
} from "./state/shared.selectors";
import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import { where, isNotNil } from "ramda";
import {
  getEmailDomain,
  getKubernetesCluster,
  getKubernetesNamespace,
  getKubernetesNamespaceAndApp,
  isLocalDev,
  getAppName,
} from "./util/domain";
import { logOut } from "./util/session";
import { useQueryClient } from "@revenue-management/pos-config-tool";
import { themeValues } from "./constants/theme";

const App = ({
  oidc,
  isLoggedIn,
  logoutFromInactivity,
  email,
  role,
  agencies,
  slug,
  brand,
  posEnabled,
  paymentPortalURL,
  loadingOverlayIsActive,
}) => {
  const initialTimeLeftToLogoutSeconds = 30;
  const [idleModalOpen, toggleIdleModal] = useState(false);
  const [logoutTimerOn, toggleLogoutTimer] = useState(false);
  const [timeLeftToLogout, setTimeLeft] = useState(initialTimeLeftToLogoutSeconds);

  // When the OktaAuth object is not memoized, it causes extraneous rerenders on the login screen and
  // results in slower performance as well as unnecessary additional calls to the Okta auth server.
  const oktaAuth = useMemo(
    () =>
      new OktaAuth({
        ...oidc,
        redirectUri: window.location.origin + oidc.redirectUri,
      }),
    [oidc]
  );

  // Begin LaunchDarkly initialization

  const hostname = window.location.hostname;
  const ldClient = useLDClient();
  const emailDomain = getEmailDomain(email);
  const isLocal = isLocalDev(hostname);
  const isProd = getKubernetesCluster(hostname) === "prod";
  const cluster = getKubernetesCluster(hostname);
  const nameSpaceAndApp = getKubernetesNamespaceAndApp(hostname);
  const namespace = getKubernetesNamespace(nameSpaceAndApp);
  const app = getAppName(hostname);

  /*
    LD context object creation memoized to ensure that useEffect call does not trigger
    on every render cycle, only when the context properties have changed
  */

  const userContext = useMemo(
    () => ({
      email, // restricted as "private attribute" in LD call, to prevent transmission of PII
      domain: emailDomain,
      role,
      agenciesList: agencies,
    }),
    [email, emailDomain, role, agencies]
  );

  const clientContext = useMemo(
    () => ({
      slug,
      brand,
      posEnabled,
      prodURL: paymentPortalURL,
    }),
    [slug, brand, posEnabled, paymentPortalURL]
  );

  const envContext = useMemo(
    () => ({
      local: isLocal,
      prod: isProd,
      hostname,
      app,
    }),
    [isLocal, isProd, hostname, app]
  );

  const k8sContext = useMemo(
    () => ({
      cluster,
      namespace,
      app,
      prod: isProd,
    }),
    [cluster, namespace, app, isProd]
  );

  /*
    Verify all properties of contexts are present before sending to LD
    Not necessary for envContext or k8sContext as those have no Redux store dependencies
  */

  const evaluateClientContext = useCallback((clientContext) => {
    const predicate = where({
      slug: isNotNil(),
      brand: isNotNil(),
      posEnabled: isNotNil(),
    });

    return predicate(clientContext);
  }, []);

  const evaluateUserContext = useCallback((userContext) => {
    const predicate = where({
      email: isNotNil(),
      domain: isNotNil(),
      role: isNotNil(),
      agenciesList: isNotNil(),
    });

    return predicate(userContext);
  }, []);

  useEffect(() => {
    const mergedContext = {
      kind: "multi",
      environment: {
        key: "environment",
        ...envContext,
      },
      ["kubernetes-environment"]: {
        key: "kubernetes-environment",
        ...k8sContext,
      },
      client: {
        key: "client",
        ...clientContext,
      },
    };

    if (evaluateClientContext(clientContext)) {
      mergedContext["client"] = {
        kind: "client",
        key: "client",
        ...clientContext,
      };
    }

    if (isLoggedIn && evaluateUserContext(userContext)) {
      mergedContext["user"] = {
        kind: "user",
        key: "user",
        _meta: {
          privateAttributes: ["email"],
        },
        ...userContext,
      };
    }

    ldClient.identify(mergedContext);
  }, [
    ldClient,
    envContext,
    k8sContext,
    clientContext,
    evaluateClientContext,
    userContext,
    evaluateUserContext,
  ]);

  const flags = useFlags();

  // Get maintenance feature flag from Launch Darkly
  const useMaintenanceScreen = flags?.["display-maintenance-screen-rev-m"];

  // End LaunchDarkly initialization

  const idleLogout = async () => {
    window.clearInterval(logoutTimers.interval);
    toggleIdleModal(false);
    toggleLogoutTimer(false);
    await logOut(oktaAuth);
    setTimeLeft(initialTimeLeftToLogoutSeconds);
    logoutFromInactivity();
  };

  const handleOnIdle = () => {
    if (!isLoggedIn) return;
    toggleIdleModal(true);
    toggleLogoutTimer(true);
  };

  useIdleTimer({
    timeout: 1000 * 60 * 15,
    onIdle: handleOnIdle,
    debounce: 500,
  });

  const logoutTimers = useLogoutTimers({
    logoutTimerOn,
    logOutAction: idleLogout,
    timeLeftToLogout,
    handleSetTimeLeft: setTimeLeft,
    initialTimeLeftToLogoutSeconds,
  });

  useEffect(() => {
    const CHECK_OKTA_SESSION_EVERY_MS = 60 * 1000; // 1 minute.

    const interval = setInterval(async () => {
      const isAuthenticated = await oktaAuth.isAuthenticated();
      if (!isAuthenticated) return;

      const exists = await oktaAuth.session.exists();
      if (!exists) await logOut(oktaAuth);
    }, CHECK_OKTA_SESSION_EVERY_MS);
    return () => clearInterval(interval);
  }, [oktaAuth]);

  const dispatch = useDispatch();
  const queryClient = useQueryClient(dispatch);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider
        theme={{
          name: "default",
          values: themeValues,
        }}
      >
        <div>
          <Overlay />
          <LoadingOverlay loadingOverlayIsActive={loadingOverlayIsActive}>
            <Routes
              oktaAuth={oktaAuth}
              useMaintenanceScreen={useMaintenanceScreen}
            />
          </LoadingOverlay>
          {!useMaintenanceScreen && (
            <IdleModal
              modalOpen={idleModalOpen}
              toggleModal={toggleIdleModal}
              timeLeftToLogout={timeLeftToLogout}
              totalLogoutTime={initialTimeLeftToLogoutSeconds}
              logout={idleLogout}
              cancelLogout={() => {
                window.clearInterval(logoutTimers.interval);
                window.clearTimeout(logoutTimers.timer);
                setTimeLeft(initialTimeLeftToLogoutSeconds);
                toggleIdleModal(false);
                toggleLogoutTimer(false);
              }}
            />
          )}
        </div>
      </ThemeProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};

export const mapStateToProps = (state) => {
  return {
    oidc: state.config.oidc,
    isLoggedIn: userIsLoggedIn(state),
    email: getUserEmail(state),
    role: getUserRole(state),
    agencies: getUserAgencies(state),
    slug: getClientSlug(state),
    brand: getClientBrand(state),
    posEnabled: getPOSEnabled(state),
    paymentPortalURL: getPaymentPortalURL(state),
    loadingOverlayIsActive: getLoadingOverlayIsActive(state),
  };
};

export const mapDispatchToProps = (dispatch) => ({
  logoutFromInactivity: () => dispatch(logoutFromInactivity()),
});

export default withOktaAuth(connect(mapStateToProps, mapDispatchToProps)(App));
